import { Store, GetterTree, MutationTree, ActionTree, Module } from "vuex";

import { RootState } from "..";
import { zoneService } from "../../services/zone.service";
import { Zone } from "../../models/zone/zone.model";
import { IZone } from "../../models/interfaces/zone/zone.model.interface";
import { IZoneDetailsRequest } from "../../models/interfaces/zone/zone-details-request.model.interface";
import { IZoneManagementState } from "../states/zone-management-state";
import { IProductCategory } from "../../models/interfaces/product/product-category.model.interface";
import { IContentType } from "../../models/interfaces/content/content-type-model.interface";
import { IZoneEnhancementsRequest } from "../../models/interfaces/zone/zone-enhancements-request.model.interface";
import { StatusType } from "@/enums/StatusType";
import { IZoneCreationRequest } from "@/models/interfaces/zone/zone-creation-request.model.interface";
import { IContentSet } from "@/models/interfaces/content/content-set.model.interface";
import { IZoneContentSet } from "@/models/interfaces/zone/zone-content-set.model.interface";

export const state: IZoneManagementState = {
  zone: new Zone()
};

export const getters: GetterTree<IZoneManagementState, RootState> = {
  zone(state) {
    return state.zone;
  }
};

export const mutations: MutationTree<IZoneManagementState> = {
  zone(state, zone: IZone) {
    state.zone = zone;
  },
  addProductCategory(state, productCategory: IProductCategory) {
    state.zone.permittedCategories.push(productCategory);
  },
  removeProductCategory(state, productCategoryId: number) {
      state.zone.permittedCategories = state.zone.permittedCategories.filter((item) => item.productCategoryId !== productCategoryId);
  },
  addContentType(state, contentType: IContentType) {
    state.zone.contentTypes.push(contentType);
  },
  removeContentType(state, contentTypeId: number) {
      state.zone.contentTypes = state.zone.contentTypes.filter((item) => item.contentTypeId !== contentTypeId);
  },
  addContentSet(state, zoneContentSet: IZoneContentSet) {
    state.zone.contentSets.push(zoneContentSet);
  },
  removeContentSet(state, contentSetId: number) {
      state.zone.contentSets = state.zone.contentSets.filter((item) => item.contentSetId !== contentSetId);
  }
};

export const actions: ActionTree<IZoneManagementState, RootState> = {
  async createZone({ commit, rootState }, zone: IZoneCreationRequest) {
    return new Promise((resolve) => {
      zoneService.create(rootState.propertyManagementModule.property.propertyId, zone)
            .then((value) => {
                if (value.success && value.result !== null) {
                    commit("zone", value.result);
                }
                resolve(value);
            });
    });
  },
  async retrieveZone({ state, commit }, zoneId: number) {
    return new Promise((resolve, reject) => {
      zoneService.getZone(zoneId)
        .then((value) => {
          if (value.success) {
            commit("zone", value.result);
          }
          resolve(value.success);
        });
    });
  },
  async updateDetails({ state }, zone: IZone) {
    return new Promise((resolve, reject) => {
        const request: IZoneDetailsRequest = {
            name: zone.name,
            description: zone.description,
            urlPattern: zone.urlPattern
        };

        zoneService.updateDetails(zone.zoneId, request).then((value) => {
          resolve(value);
      });
    });
  },
  async updateContentEnhancements({ state }, zone: IZone) {
    return new Promise((resolve, reject) => {
        const request: IZoneEnhancementsRequest = {
          allowConquest: zone.allowConquest,
          allowMultivariate: zone.allowMultivariate
        };

        zoneService.updateContentEnhancements(zone.zoneId, request).then((value) => {
          resolve(value);
      });
    });
  },
  async addProductCategory({ commit, state, rootState }, productCategoryId: number) {
    return new Promise((resolve) => {
      zoneService.addProductCategory(state.zone.zoneId, productCategoryId)
        .then((value) => {
          if (value) {
              commit("addProductCategory", rootState.productModule.productCategories.filter((item) => item.productCategoryId === productCategoryId)[0]);
              resolve(value);
          } else {
            resolve(false);
          }
        });
    });
  },
  async removeProductCategory({ commit, state }, productCategoryId: number) {
    return new Promise((resolve) => {
      zoneService.removeProductCategory(state.zone.zoneId, productCategoryId)
        .then((value) => {
            if (value) {
                commit("removeProductCategory", productCategoryId);
                resolve(value);
            } else {
              resolve(false);
            }
        });
    });
  },
  async addContentType({ commit, state, rootState }, contentTypeId: number) {
    return new Promise((resolve) => {
      zoneService.addContentType(state.zone.zoneId, contentTypeId)
        .then((value) => {
          if (value) {
              commit("addContentType", rootState.contentTypeModule.contentTypes.filter((item) => item.contentTypeId === contentTypeId)[0]);
              resolve(value);
          } else {
            resolve(false);
          }
        });
    });
  },
  async removeContentType({ commit, state }, contentTypeId: number) {
    return new Promise((resolve) => {
      zoneService.removeContentType(state.zone.zoneId, contentTypeId)
        .then((value) => {
            if (value) {
                commit("removeContentType", contentTypeId);
                resolve(value);
            } else {
              resolve(false);
            }
        });
    });
  },
  async pauseZone({ dispatch, rootState },  zone: IZone) {
    return new Promise((resolve) => {
      zone.state.statusType = StatusType.Paused;
      zoneService.updateState(zone.zoneId, zone.state).then((value) => {
          dispatch("propertyManagementModule/retrieveProperty", rootState.propertyManagementModule.property.propertyId, { root: true }).then((value) => {
            resolve(true);
          });
        });
    });
  },
  async setZoneLive({ dispatch, rootState },  zone: IZone) {
    return new Promise((resolve) => {
      zone.state.statusType = StatusType.Live;
      zoneService.updateState(zone.zoneId, zone.state).then((value) => {
          dispatch("propertyManagementModule/retrieveProperty", rootState.propertyManagementModule.property.propertyId, { root: true }).then((value) => {
            resolve(true);
          });
        });
    });
  },
  async addContentSet({ commit, state, rootState }, contentSet: IContentSet) {
    return new Promise((resolve) => {
      zoneService.addContentSet(state.zone.zoneId, contentSet.contentSetId)
        .then((value) => {
          if (value) {
            const zoneContentSet: IZoneContentSet = {
              contentSet,
              contentSetId: contentSet.contentSetId,
              zoneId: state.zone.zoneId,
              processId: null,
              process: null
            };
            commit("addContentSet", zoneContentSet);
            resolve(value);
          } else {
            resolve(false);
          }
        });
    });
  },
  async removeContentSet({ commit, state }, contentSet: IContentSet) {
    return new Promise((resolve) => {
      zoneService.removeContentSet(state.zone.zoneId, contentSet.contentSetId)
        .then((value) => {
            if (value) {
                commit("removeContentSet", contentSet.contentSetId);
                resolve(value);
            } else {
              resolve(false);
            }
        });
    });
  },
};

export const zoneManagementModule: Module<IZoneManagementState, RootState> = {
  state, getters, mutations, actions, namespaced: true
};
